import { useEffect, useRef } from "react";
import { useCustom, useCustomMutation } from "@refinedev/core";

import { heeftEigenTaalkeuze, i18nProvider, SUPPORTED_LOCALES } from "./i18n/i18nProvider";

// ── De taalkeuze gaat mee met het account (Maarten, 2026-10-13) ─────────────────────────────────────
//
// De regel: een nieuwe gebruiker ziet de taal van zijn systeem. Kiest hij in de lijst een andere taal, dan wordt
// die bewaard en gaat hij vóór de systeemtaal.
//
// Die keuze stond alleen in de browser. Op een andere computer, in een andere browser of na het wissen van de
// browsergegevens was hij weg en nam de systeemtaal het weer over: wie Nederlands gekozen had zat dan toch in een
// Engelse backoffice. Daarom wordt de keuze na het inloggen ook bij het account bewaard, en haalt elk apparaat hem
// daar vandaan. De laatste bewuste keuze wint, waar die ook gemaakt is.

const NET_GEKOZEN = "fawkes.locale.gekozen";

/** Onthoud dat de gebruiker zojuist zelf een taal koos; na het inloggen gaat die naar het account. */
export function markeerTaalGekozen(): void {
  try {
    globalThis.sessionStorage?.setItem(NET_GEKOZEN, "1");
  } catch {
    /* zonder sessionStorage blijft de keuze in deze browser gelden */
  }
}

function netGekozen(): boolean {
  try {
    return globalThis.sessionStorage?.getItem(NET_GEKOZEN) === "1";
  } catch {
    return false;
  }
}

function wisNetGekozen(): void {
  try {
    globalThis.sessionStorage?.removeItem(NET_GEKOZEN);
  } catch {
    /* niets te wissen */
  }
}

/** Staat in de ingelogde applicatie; toont niets. */
export function TaalVanAccount({ herlaad = () => globalThis.location?.reload() }: { herlaad?: () => void }) {
  const { data, isSuccess } = useCustom<{ taal?: string | null }>({
    url: "auth/me/taal", method: "get", errorNotification: false,
    queryOptions: { retry: false, staleTime: Infinity },
  });
  const { mutate } = useCustomMutation();
  const gedaan = useRef(false);
  const bewaard = data?.data?.taal ?? null;

  useEffect(() => {
    if (!isSuccess || gedaan.current) return;
    gedaan.current = true;
    const nu = i18nProvider.getLocale();
    // Zojuist gekozen op dit apparaat, of een keuze van vóór dit bestond: die gaat naar het account.
    if (netGekozen() || (bewaard == null && heeftEigenTaalkeuze())) {
      wisNetGekozen();
      if (bewaard !== nu) {
        mutate({ url: "auth/me/taal", method: "put", values: { taal: nu }, successNotification: false, errorNotification: false });
      }
      return;
    }
    // Anders geldt wat bij het account staat, ook als het systeem van dit apparaat een andere taal heeft.
    if (bewaard && bewaard !== nu && (SUPPORTED_LOCALES as readonly string[]).includes(bewaard)) {
      void i18nProvider.changeLocale!(bewaard).then(herlaad);
    }
  }, [isSuccess, bewaard, mutate, herlaad]);

  return null;
}
